/**
 * @file LabelQtyStepper.tsx
 * @description Степпер количества этикеток «[−] 24 [+]» 56px (phase19.22,
 *   ui-design §1, §2; правила warehouse-UI: тач ≥ 56px).
 *
 * Отдельный компонент: он живёт и в диалоге печати, и в строке журнала
 * остатков у отмеченной партии. Число вводится и руками — «24» на 1 кнопкой
 * не набирают. Границы 0..2000 (потолок строки на сервере).
 */

import { Minus, Plus } from "lucide-react";

import { Button } from "../../ui/button";
import { LABEL_MAX_QTY_PER_LINE } from "../../../lib/label-sheets";

interface Props {
  value: number;
  onChange: (value: number) => void;
  /** Подпись для скринридера: «Этикеток: Иван-чай 100 г». */
  ariaLabel: string;
  min?: number;
  disabled?: boolean;
}

/** Целое в границах [min, потолок строки]. */
function clamp(value: number, min: number): number {
  if (!Number.isFinite(value)) return min;
  return Math.min(Math.max(min, Math.trunc(value)), LABEL_MAX_QTY_PER_LINE);
}

export function LabelQtyStepper({ value, onChange, ariaLabel, min = 0, disabled }: Props) {
  return (
    <div className="inline-flex items-center gap-1">
      <Button
        type="button"
        variant="outline"
        className="size-14 p-0"
        onClick={() => onChange(clamp(value - 1, min))}
        disabled={disabled || value <= min}
        aria-label="Меньше"
      >
        <Minus className="size-5" />
      </Button>
      <input
        type="text"
        inputMode="numeric"
        value={String(value)}
        onChange={(e) => onChange(clamp(Number(e.target.value.replace(/\D/g, "") || "0"), min))}
        disabled={disabled}
        aria-label={ariaLabel}
        className="h-14 w-16 rounded-lg bg-surface-lowest text-center font-mono text-base tabular-nums ring-ghost focus:outline-none focus:ring-2 focus:ring-primary"
      />
      <Button
        type="button"
        variant="outline"
        className="size-14 p-0"
        onClick={() => onChange(clamp(value + 1, min))}
        disabled={disabled || value >= LABEL_MAX_QTY_PER_LINE}
        aria-label="Больше"
      >
        <Plus className="size-5" />
      </Button>
    </div>
  );
}
